import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Send, 
  MessageCircle,
  Search,
  MoreVertical,
  Image as ImageIcon,
  Loader2,
  Check,
  CheckCheck,
  Smile,
  Star,
  Trophy,
  ShoppingBag,
  Sparkles,
  Trash2,
  X,
  CheckSquare
} from "lucide-react";

const MessagesPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [stickerPacks, setStickerPacks] = useState([]);
  const [ownedStickers, setOwnedStickers] = useState([]);
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const [showStickerShop, setShowStickerShop] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const messagesEndRef = useRef(null);
  const refreshInterval = useRef(null);

  useEffect(() => {
    fetchConversations();
    fetchStickerPacks();
    fetchOwnedStickers();
    
    // Her 5 saniyede mesajları yenile
    refreshInterval.current = setInterval(() => {
      if (selectedUser) {
        fetchMessages(selectedUser.id, true);
      }
      fetchConversations();
    }, 5000);
    
    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, []);

  // URL'den gelen userId ile konuşmayı otomatik aç
  useEffect(() => {
    if (userId && !selectedUser) {
      openConversationWithUser(userId);
    }
  }, [userId]);

  const openConversationWithUser = async (targetUserId) => {
    try {
      // Önce kullanıcı bilgilerini al
      const userResponse = await axios.get(`${API}/users/profile/${targetUserId}`);
      if (userResponse.data) {
        const targetUser = {
          id: userResponse.data.id,
          name: userResponse.data.name,
          profileImage: userResponse.data.profileImage
        };
        setSelectedUser(targetUser);
        fetchMessages(targetUserId);
      }
    } catch (error) {
      toast.error("Kullanıcı bulunamadı");
      navigate('/messages');
    }
  };

  useEffect(() => {
    if (selectedUser) {
      fetchMessages(selectedUser.id);
    }
  }, [selectedUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (otherUserId, silent = false) => {
    try {
      const response = await axios.get(`${API}/messages/conversation/${otherUserId}`);
      setMessages(response.data.messages || []);
      if (response.data.otherUser && !silent) {
        setSelectedUser(response.data.otherUser);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const fetchStickerPacks = async () => {
    try {
      const response = await axios.get(`${API}/stickers/packs`);
      setStickerPacks(response.data.packs || []);
    } catch (error) {
      console.error("Error fetching sticker packs:", error);
    }
  };

  const fetchOwnedStickers = async () => {
    try {
      const response = await axios.get(`${API}/stickers/owned`);
      setOwnedStickers(response.data.stickers || []);
    } catch (error) {
      console.error("Error fetching owned stickers:", error);
    }
  };

  const handleSendMessage = async (stickerId = null, stickerEmoji = null) => {
    if (!selectedUser) return;
    if (!newMessage.trim() && !stickerId) return;
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/messages/send`, {
        receiverId: selectedUser.id,
        content: stickerId ? "" : newMessage,
        stickerId: stickerId
      });
      
      if (response.data.success) {
        setMessages(prev => [...prev, response.data.message]);
        setNewMessage("");
        setShowStickerPicker(false);
        
        if (response.data.popularityBonus > 0) {
          toast.success(`🌟 +${response.data.popularityBonus} popülerlik puanı gönderildi!`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Mesaj gönderilemedi");
    } finally {
      setSending(false);
    }
  };

  const handlePurchaseSticker = async (packId) => {
    try {
      const response = await axios.post(`${API}/stickers/packs/${packId}/purchase`);
      if (response.data.success) {
        toast.success("Sticker paketi satın alındı!");
        fetchStickerPacks();
        fetchOwnedStickers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Satın alma başarısız");
    }
  };

  const toggleMessageSelection = (messageId) => {
    setSelectedMessages(prev => 
      prev.includes(messageId) 
        ? prev.filter(id => id !== messageId)
        : [...prev, messageId]
    );
  };

  const handleDeleteSelectedMessages = async () => {
    if (selectedMessages.length === 0) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/messages/bulk`, {
        data: selectedMessages
      });
      
      if (response.data.success) {
        toast.success(`${response.data.deletedCount} mesaj silindi`);
        setMessages(prev => prev.filter(msg => !selectedMessages.includes(msg.id)));
        setSelectedMessages([]);
        setSelectionMode(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Mesajlar silinemedi");
    } finally {
      setDeleting(false);
    }
  };

  const cancelSelectionMode = () => {
    setSelectionMode(false);
    setSelectedMessages([]);
  };

  const selectAllOwnMessages = () => {
    const ownMessageIds = messages
      .filter(msg => msg.senderId === user?.id)
      .map(msg => msg.id);
    setSelectedMessages(ownMessageIds);
  };

  const selectConversation = async (conv) => {
    setSelectedUser({
      id: conv.otherUserId,
      name: conv.otherUserName,
      profileImage: conv.otherUserImage
    });
    fetchMessages(conv.otherUserId);
  };

  const filteredConversations = conversations.filter(conv =>
    conv.otherUserName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        <div className="flex gap-4 h-[calc(100vh-150px)]">
          {/* Konuşmalar Listesi - Mobilde seçili kullanıcı yokken göster */}
          <Card className={`w-full md:w-96 flex flex-col ${selectedUser ? 'hidden md:flex' : 'flex'}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-emerald-600" />
                  Mesajlar
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowStickerShop(true)}>
                  <ShoppingBag className="h-4 w-4" />
                </Button>
              </div>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden p-0">
              <ScrollArea className="h-full touch-pan-y">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="text-center py-8 px-4">
                    <MessageCircle className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-500 text-sm">Henüz mesajınız yok</p>
                  </div>
                ) : (
                  filteredConversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => selectConversation(conv)}
                      className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 border-b transition-colors select-none ${
                        selectedUser?.id === conv.otherUserId ? 'bg-emerald-50' : ''
                      }`}
                      style={{ touchAction: 'manipulation' }}
                    >
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={conv.otherUserImage} />
                        <AvatarFallback className="bg-emerald-100 text-emerald-700">
                          {conv.otherUserName?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium truncate">{conv.otherUserName}</p>
                          <span className="text-xs text-gray-400">
                            {formatTime(conv.lastMessageAt)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 truncate">{conv.lastMessage}</p>
                      </div>
                      {conv.unreadCount > 0 && (
                        <Badge className="bg-emerald-500">{conv.unreadCount}</Badge>
                      )}
                    </div>
                  ))
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Mesaj Alanı - Mobilde seçili kullanıcı varken göster */}
          <Card className={`flex-1 flex flex-col ${selectedUser ? 'flex' : 'hidden md:flex'}`}>
            {selectedUser ? (
              <>
                {/* Başlık */}
                <CardHeader className="border-b py-3">
                  <div className="flex items-center gap-3">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setSelectedUser(null)}
                      className="md:hidden"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={selectedUser.profileImage} />
                      <AvatarFallback className="bg-emerald-100 text-emerald-700">
                        {selectedUser.name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-medium">{selectedUser.name}</p>
                      {selectedUser.popularityPoints > 0 && (
                        <div className="flex items-center gap-1 text-xs text-amber-600">
                          <Star className="h-3 w-3 fill-amber-400" />
                          {selectedUser.popularityPoints} popülerlik
                        </div>
                      )}
                    </div>
                    
                    {/* Seçim modu kontrolleri */}
                    {selectionMode ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">
                          {selectedMessages.length} seçili
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={selectAllOwnMessages}
                          title="Tüm mesajlarımı seç"
                        >
                          <CheckSquare className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={handleDeleteSelectedMessages}
                          disabled={selectedMessages.length === 0 || deleting}
                        >
                          {deleting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={cancelSelectionMode}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectionMode(true)}
                          title="Mesaj seç ve sil"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/profile/${selectedUser.id}`)}
                        >
                          Profil
                        </Button>
                      </>
                    )}
                  </div>
                </CardHeader>

                {/* Mesajlar */}
                <CardContent className="flex-1 overflow-hidden p-0">
                  <ScrollArea className="h-full p-4">
                    <div className="space-y-4">
                      {messages.map((msg, idx) => {
                        const isOwn = msg.senderId === user?.id;
                        const isSelected = selectedMessages.includes(msg.id);
                        return (
                          <div
                            key={msg.id || idx}
                            className={`flex ${isOwn ? 'justify-end' : 'justify-start'} items-center gap-2`}
                          >
                            {/* Seçim checkbox'ı (sadece kendi mesajlarımız için) */}
                            {selectionMode && isOwn && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleMessageSelection(msg.id);
                                }}
                                className={`flex items-center justify-center w-6 h-6 rounded border-2 transition-colors cursor-pointer shrink-0 ${isOwn ? 'order-1' : ''} ${
                                  isSelected 
                                    ? 'bg-emerald-500 border-emerald-500' 
                                    : 'border-gray-300 hover:border-emerald-400 bg-white'
                                }`}
                              >
                                {isSelected && <Check className="h-4 w-4 text-white" />}
                              </button>
                            )}
                            
                            <div 
                              className={`max-w-[70%] ${isOwn ? 'order-2' : ''} ${selectionMode && isOwn ? 'cursor-pointer' : ''}`}
                              onClick={() => selectionMode && isOwn && toggleMessageSelection(msg.id)}
                            >
                              <div
                                className={`rounded-2xl px-4 py-2 ${
                                  isOwn
                                    ? 'bg-emerald-500 text-white rounded-tr-sm'
                                    : 'bg-gray-100 text-gray-800 rounded-tl-sm'
                                } ${isSelected ? 'ring-2 ring-emerald-300' : ''}`}
                              >
                                {msg.stickerEmoji ? (
                                  <span className="text-4xl">{msg.stickerEmoji}</span>
                                ) : (
                                  <p>{msg.content}</p>
                                )}
                              </div>
                              <div className={`flex items-center gap-1 mt-1 text-xs text-gray-400 ${isOwn ? 'justify-end' : ''}`}>
                                <span>{formatTime(msg.createdAt)}</span>
                                {isOwn && (
                                  msg.isRead ? (
                                    <CheckCheck className="h-3 w-3 text-blue-500" />
                                  ) : (
                                    <Check className="h-3 w-3" />
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>
                </CardContent>

                {/* Mesaj Gönderme */}
                <div className="border-t p-3">
                  <div className="flex items-center gap-2">
                    {/* Sticker Picker */}
                    <Popover open={showStickerPicker} onOpenChange={setShowStickerPicker}>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="icon" className="shrink-0">
                          <Smile className="h-5 w-5 text-gray-500" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-0" align="start">
                        <div className="p-3 border-b">
                          <p className="font-medium text-sm">Stickerlar</p>
                          <p className="text-xs text-gray-500">Sticker gönder, popülerlik puanı kazan!</p>
                        </div>
                        <ScrollArea className="h-64">
                          {ownedStickers.length === 0 ? (
                            <div className="p-4 text-center">
                              <Sparkles className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                              <p className="text-sm text-gray-500">Henüz sticker paketiniz yok</p>
                              <Button 
                                size="sm" 
                                className="mt-2"
                                onClick={() => {
                                  setShowStickerPicker(false);
                                  setShowStickerShop(true);
                                }}
                              >
                                Mağazaya Git
                              </Button>
                            </div>
                          ) : (
                            <div className="grid grid-cols-4 gap-2 p-3">
                              {ownedStickers.map((sticker) => (
                                <button
                                  key={sticker.id}
                                  onClick={() => handleSendMessage(sticker.id, sticker.emoji)}
                                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-2xl"
                                  title={`${sticker.name} (+${sticker.popularityBonus} puan)`}
                                >
                                  {sticker.emoji}
                                </button>
                              ))}
                            </div>
                          )}
                        </ScrollArea>
                      </PopoverContent>
                    </Popover>

                    <Input
                      placeholder="Mesajınızı yazın..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                      className="flex-1"
                    />
                    <Button 
                      onClick={() => handleSendMessage()}
                      disabled={sending || !newMessage.trim()}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {sending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Mesajlaşmak için bir konuşma seçin</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Sticker Mağazası Dialog */}
      <Dialog open={showStickerShop} onOpenChange={setShowStickerShop}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-emerald-600" />
              Sticker Mağazası
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {stickerPacks.map((pack) => (
              <Card key={pack.id} className={pack.isOwned ? 'border-emerald-300 bg-emerald-50' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold">{pack.name}</h3>
                        {pack.isOwned && (
                          <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                            Sahipsin
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{pack.description}</p>
                      <div className="flex gap-2 mt-3">
                        {pack.stickers?.slice(0, 6).map((sticker) => (
                          <span 
                            key={sticker.id} 
                            className="text-2xl"
                            title={`${sticker.name} (+${sticker.popularityBonus} puan)`}
                          >
                            {sticker.emoji}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      {pack.isOwned ? (
                        <span className="text-emerald-600 font-medium">✓ Satın Alındı</span>
                      ) : (
                        <>
                          <p className="font-bold text-lg">{pack.price} 💰</p>
                          <Button 
                            size="sm" 
                            className="mt-2 bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handlePurchaseSticker(pack.id)}
                            disabled={user?.credits < pack.price}
                          >
                            Satın Al
                          </Button>
                          {user?.credits < pack.price && (
                            <p className="text-xs text-red-500 mt-1">Yetersiz kredi</p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-gray-500">
              Krediniz: <span className="font-bold text-emerald-600">{user?.credits || 0} 💰</span>
            </div>
            <Button variant="outline" onClick={() => navigate('/credits')}>
              Kredi Satın Al
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagesPage;
